import { type SerializedFiltersData } from '@/components/products-listing/filters'
import {
  type Order,
  type OrderBy,
  type PaginationOrder,
} from '@/hooks/useSortPagination'

import {
  FilterCreationDate,
  FilterKit,
  FilterNull,
  FilterStatus,
  FilterStoreVisibility,
  type SortByEvent,
} from '../customEvents'

type FiltersToTransform = {
  kitFilter: SerializedFiltersData['FilterKit']
  statusFilter: SerializedFiltersData['FilterStatus']
  visibleFilter: SerializedFiltersData['FilterVisible']
  createdAtFilter: SerializedFiltersData['FilterCreatedAt']
  brandFilter: SerializedFiltersData['FilterBrand']
  categoryFilter: SerializedFiltersData['FilterCategory']
  salesChannelFilter: SerializedFiltersData['FilterSalesChannel']
  sellerFilter: SerializedFiltersData['FilterSeller']
  sortPagination: string
  isPinned: boolean
}

function transformStringfiedBooleanFilterValueToEventProperty<EventProperty>(
  filterValue: '' | 'true' | 'false',
  eventPropertyForTruthyValue: EventProperty,
  eventPropertyForFalsyValue: EventProperty
) {
  if (filterValue === '') return FilterNull

  return filterValue === 'true'
    ? eventPropertyForTruthyValue
    : eventPropertyForFalsyValue
}

export const transformProductListFiltersToEventProperties = ({
  kitFilter,
  statusFilter,
  visibleFilter,
  createdAtFilter,
  brandFilter,
  categoryFilter,
  salesChannelFilter,
  sellerFilter,
  sortPagination,
  isPinned,
}: FiltersToTransform) => {
  const filterKitEvent = transformStringfiedBooleanFilterValueToEventProperty(
    kitFilter,
    FilterKit.activeKit,
    FilterKit.inactiveKit
  )

  const filterStatusEvent =
    transformStringfiedBooleanFilterValueToEventProperty(
      statusFilter,
      FilterStatus.publishedOrPending,
      FilterStatus.inactive
    )

  const storeVisibilityEvent =
    transformStringfiedBooleanFilterValueToEventProperty(
      visibleFilter,
      FilterStoreVisibility.visible,
      FilterStoreVisibility.hidden
    )

  const creationDateEvent =
    createdAtFilter.appliedItem !== ''
      ? (FilterCreationDate[createdAtFilter.appliedItem] ??
        FilterCreationDate.custom)
      : FilterNull

  const orderParams = new URLSearchParams(sortPagination)

  return {
    'Filter Brand': Boolean(brandFilter.length),
    'Filter Category': Boolean(categoryFilter.length),
    'Filter Creation Date': creationDateEvent,
    'Filter Kit': filterKitEvent,
    'Filter Status': filterStatusEvent,
    'Filter Sales Channel': Boolean(salesChannelFilter.length),
    'Filter Seller': Boolean(sellerFilter.length),
    'Filter Store Visibility': storeVisibilityEvent,
    'Sort by': transformSortPaginationToEventProperties({
      order: orderParams.get('order') as Order,
      orderBy: orderParams.get('orderBy') as OrderBy,
    }),
    'Is Pinned': isPinned,
  }
}

export const transformSortPaginationToEventProperties = ({
  order,
  orderBy,
}: PaginationOrder) => `${orderBy}:${order}`.toLowerCase() as SortByEvent
